// src/store/artworksSlice.js
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { API_BASE_CANDIDATES, API_BASE_URL } from '../config/apiConfig';

// ---------- helpers ----------
const IMG_FIELDS = ['imageUrl','imageURL','image','thumbnailUrl','url'];
const CARDINALS = ['midnight','morning','noon','evening'];

function toPublicHttps(raw) {
  if (!raw) return '';
  if (/^gs:\/\//i.test(raw)) {
    const parts = raw.replace(/^gs:\/\//i,'').split('/');
    const bucket = parts.shift();
    return `https://storage.googleapis.com/${bucket}/${encodeURI(parts.join('/'))}`;
  }
  if (typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      /^http:\/\//i.test(raw)) {
    return raw.replace(/^http:/,'https:');
  }
  return raw;
}
function filenameOf(url = '') {
  try { return new URL(url).pathname.split('/').pop() || ''; }
  catch { return String(url).split('/').pop() || ''; }
}
// Parse our new filename format: YYYY-MM-DD_<cardinal>_chronicle_YYYY-MM-DDThh-mm-ss-SSSZ.mp3
function parseFromFilename(name = '') {
  const m = name.match(/^(\d{4}-\d{2}-\d{2})_(midnight|morning|noon|evening)_chronicle_([0-9T-]+)Z/i);
  if (!m) return {};
  const [, ymd, card, tsPart] = m;
  const iso = tsPart
    .replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3')
    .replace(/-(\d{2})-(\d{2})-(\d{3})$/, ':$1:$2.$3') + 'Z';
  return { ymd, cardinalTime: card.toLowerCase(), iso };
}
function normalizeItem(raw) {
  const id = raw.id || raw._id || raw.key || raw.slug || raw.name || raw.title;
  const imgRaw = IMG_FIELDS.map(k => raw?.[k]).find(Boolean);
  const imageUrl = imgRaw || '';
  const safeImageUrl = toPublicHttps(imgRaw || '');
  const createdAt = raw.createdAt || raw.timestamp || raw.date || raw.timeCreated || raw.updated || null;

  // Try to infer date/cardinal from filename when missing
  const fromName = parseFromFilename(raw.name || filenameOf(raw.mp3Url || raw.url || '')) || {};
  const cardinalTime = (raw.cardinalTime || raw.timeOfDay || (fromName.cardinalTime) || '').toLowerCase();
  const createdAtIso = createdAt || fromName.iso || null;

  return {
    ...raw,
    id,
    imageUrl,
    safeImageUrl,       // <— use this in UI
    createdAt: createdAtIso,
    cardinalTime: CARDINALS.includes(cardinalTime) ? cardinalTime : undefined,
  };
}

// ---------- async thunk with stale-guard ----------
export const fetchArtworks = createAsyncThunk(
  'artworks/fetchAll',
  async (_arg, thunkAPI) => {
    const baseCandidates = (API_BASE_CANDIDATES || [API_BASE_URL]).map(
      (b) => (b || '').replace(/\/+$/, '')
    );
    const guesses = Array.from(new Set([
      ...baseCandidates.flatMap((base) => [
        base ? `${base}/api/artworks` : '/api/artworks',
        base ? `${base}/artworks` : '/artworks',
      ]),
      '/api/artworks',
      '/artworks',
    ].filter(Boolean)));

    let data = null, hit = null;
    for (const url of guesses) {
      try {
        const r = await fetch(url, { credentials: 'include' });
        if (r.ok) { data = await r.json(); hit = url; break; }
      } catch (_) {}
    }
    if (!data) throw new Error('Unable to fetch artworks from known endpoints');

    const list = Array.isArray(data.items) ? data.items :
                 Array.isArray(data.artworks) ? data.artworks :
                 Array.isArray(data) ? data : [];
    return { list, source: hit };
  },
  {
    condition: (_arg, { getState, requestId }) => {
      const { loadingRequestId, status } = getState().artworks || {};
      // ignore if a different request is already in-flight
      return status !== 'loading' || loadingRequestId === requestId;
    }
  }
);

export const fetchRecipeForArtwork = createAsyncThunk(
  'artworks/fetchRecipe',
  async (artworkId, { getState, rejectWithValue }) => {
    if (!artworkId) {
      return rejectWithValue('Artwork id is required to generate a recipe.');
    }

    const state = getState();
    const existing = state?.artworks?.byId?.[artworkId];
    if (existing?.sampleRecipe) {
      return { id: artworkId, recipe: existing.sampleRecipe, fromCache: true };
    }

    const baseCandidates = (API_BASE_CANDIDATES || [API_BASE_URL]).map(
      (b) => (b || '').replace(/\/+$/, '')
    );
    const encodedId = encodeURIComponent(artworkId);
    const attempts = Array.from(new Set([
      ...baseCandidates.flatMap((base) => [
        base ? `${base}/api/artworks/${encodedId}/recipe` : `/api/artworks/${encodedId}/recipe`,
        base ? `${base}/artworks/${encodedId}/recipe` : `/artworks/${encodedId}/recipe`,
      ]),
      `/api/artworks/${encodedId}/recipe`,
      `/artworks/${encodedId}/recipe`,
    ].filter(Boolean)));

    let lastError = null;

    for (const url of attempts) {
      try {
        const response = await fetch(url, { credentials: 'include' });
        let payload = null;
        try {
          payload = await response.json();
        } catch (parseErr) {
          if (!response.ok) {
            throw new Error(`Failed to fetch recipe (${response.status}).`);
          }
          throw new Error('Recipe response was not valid JSON.');
        }

        if (!response.ok) {
          throw new Error(payload?.error || `Failed to fetch recipe (${response.status}).`);
        }

        const recipe = payload?.recipe || payload?.artwork?.sampleRecipe || null;
        if (!recipe) {
          throw new Error('Recipe payload was empty.');
        }

        return { id: artworkId, recipe };
      } catch (err) {
        lastError = err;
      }
    }

    const message = lastError?.message || 'Failed to generate recipe for this artwork.';
    return rejectWithValue(message);
  },
  {
    condition: (artworkId, { getState }) => {
      if (!artworkId) return false;
      const { recipeStatus } = getState().artworks || {};
      return recipeStatus !== 'loading';
    },
  }
);

// ---------- slice ----------
const initialState = {
  byId: {},
  allIds: [],
  selectedId: null,
  currentViewMode: 'selection',     // 'selection' | 'analysis' | 'recipe'
  status: 'idle',                   // 'idle' | 'loading' | 'succeeded' | 'failed'
  loadingRequestId: null,
  error: null,
  recipeStatus: 'idle',
  recipeError: null,
  recipeRequestId: null,
  recipeForId: null,
  savedRecipes: [],
  savedRecipeIds: [],
};

const artworksSlice = createSlice({
  name: 'artworks',
  initialState,
  reducers: {
    setViewMode(state, action) {
      state.currentViewMode = action.payload || 'selection';
    },
    selectArtworkId(state, action) {
      state.selectedId = action.payload || null;
    },
    clearError(state) { state.error = null; },
    addArtwork(state, action) {
      const item = normalizeItem(action.payload || {});
      if (!item.id) return;
      const prev = state.byId[item.id] || {};
      // Never downgrade good URLs
      const merged = {
        ...prev,
        ...item,
        imageUrl: item.imageUrl || prev.imageUrl || '',
        safeImageUrl: item.safeImageUrl || prev.safeImageUrl || '',
        sampleRecipe: {
          ...(prev.sampleRecipe || {}),
          ...(item.sampleRecipe || {}),
        },
        nutritionalInfo: {
          ...(prev.nutritionalInfo || {}),
          ...(item.nutritionalInfo || {}),
        },
        identifiedIngredients: item.identifiedIngredients || prev.identifiedIngredients || [],
        servings: item.servings || prev.servings || 4,
        prepTimeMinutes: item.prepTimeMinutes || prev.prepTimeMinutes || null,
        cookTimeMinutes: item.cookTimeMinutes || prev.cookTimeMinutes || null,
      };
      state.byId[item.id] = merged;
      if (!state.allIds.includes(item.id)) state.allIds.unshift(item.id);
      state.selectedId = item.id; // optional: focus freshly added
    },
    removeArtwork(state, action) {
      const id = action.payload;
      if (!id || !state.byId[id]) return;
      delete state.byId[id];
      state.allIds = state.allIds.filter(x => x !== id);
      if (state.selectedId === id) {
        state.selectedId = state.allIds[0] || null;
      }
      state.savedRecipes = state.savedRecipes.filter(saved => saved.id !== id);
    },
    updateRecipe(state, action) {
      const { id, recipe = {}, meta = {} } = action.payload || {};
      if (!id || !state.byId[id]) return;
      const current = state.byId[id];
      current.sampleRecipe = {
        ...(current.sampleRecipe || {}),
        ...recipe,
      };
      Object.assign(current, meta);
    },
    saveRecipe(state, action) {
      const id = action.payload;
      if (!id || !state.byId[id]) return;
      const snapshot = {
        id,
        title: state.byId[id]?.sampleRecipe?.title || state.byId[id]?.title || 'Saved Recipe',
        savedAt: new Date().toISOString(),
        recipe: JSON.parse(JSON.stringify(state.byId[id]?.sampleRecipe || {})),
        nutritionalInfo: { ...(state.byId[id]?.nutritionalInfo || {}) },
      };
      state.savedRecipes = [snapshot, ...state.savedRecipes.filter((entry) => entry.id !== id)];
    },
    removeSavedRecipe(state, action) {
      const id = action.payload;
      if (!id) return;
      state.savedRecipes = state.savedRecipes.filter((entry) => entry.id !== id);
    },
    setSavedRecipes(state, action) {
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      const unique = Array.from(new Set(incoming.map((id) => String(id))));
      state.savedRecipeIds = unique;
    },
    toggleRecipeSaved(state, action) {
      const id = action.payload;
      if (!id) return;
      const stringId = String(id);
      const exists = state.savedRecipeIds.includes(stringId);
      state.savedRecipeIds = exists
        ? state.savedRecipeIds.filter((savedId) => savedId !== stringId)
        : [...state.savedRecipeIds, stringId];
    },
    updateArtworkRecipe(state, action) {
      const { id, recipe } = action.payload || {};
      if (!id || !recipe) return;
      const stringId = String(id);
      const prev = state.byId[stringId] || { id: stringId };
      state.byId[stringId] = {
        ...prev,
        sampleRecipe: recipe,
      };
      if (!state.allIds.includes(stringId)) {
        state.allIds.unshift(stringId);
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchArtworks.pending, (state, action) => {
        state.status = 'loading';
        state.loadingRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(fetchArtworks.fulfilled, (state, action) => {
        // stale-guard: accept only the active request
        if (state.loadingRequestId && state.loadingRequestId !== action.meta.requestId) return;

        const incoming = (action.payload?.list || []).map(normalizeItem);
        for (const it of incoming) {
          if (!it.id) continue;
          const prev = state.byId[it.id] || {};
          // merge without clobbering good fields
          state.byId[it.id] = {
            ...prev,
            ...it,
            imageUrl: it.imageUrl || prev.imageUrl || '',
            safeImageUrl: it.safeImageUrl || prev.safeImageUrl || '',
            sampleRecipe: {
              ...(prev.sampleRecipe || {}),
              ...(it.sampleRecipe || {}),
            },
            nutritionalInfo: {
              ...(prev.nutritionalInfo || {}),
              ...(it.nutritionalInfo || {}),
            },
            identifiedIngredients: it.identifiedIngredients || prev.identifiedIngredients || [],
            servings: it.servings || prev.servings || prev.sampleRecipe?.servings || 4,
            prepTimeMinutes: it.prepTimeMinutes || prev.prepTimeMinutes || null,
            cookTimeMinutes: it.cookTimeMinutes || prev.cookTimeMinutes || null,
          };
          if (!state.allIds.includes(it.id)) state.allIds.push(it.id);
        }
        // keep newest first (createdAt may be null—push those to the end)
        state.allIds.sort((a, b) => {
          const A = state.byId[a]?.createdAt ? Date.parse(state.byId[a].createdAt) : 0;
          const B = state.byId[b]?.createdAt ? Date.parse(state.byId[b].createdAt) : 0;
          return B - A;
        });

        state.status = 'succeeded';
        state.loadingRequestId = null;
        state.error = null;
      })
      .addCase(fetchArtworks.rejected, (state, action) => {
        state.status = 'failed';
        state.loadingRequestId = null;
        state.error = action.error?.message || 'Failed to fetch artworks';
      })
      .addCase(fetchRecipeForArtwork.pending, (state, action) => {
        state.recipeStatus = 'loading';
        state.recipeError = null;
        state.recipeRequestId = action.meta.requestId;
        state.recipeForId = action.meta.arg || null;
        if (action.meta.arg) {
          state.selectedId = action.meta.arg;
        }
      })
      .addCase(fetchRecipeForArtwork.fulfilled, (state, action) => {
        if (state.recipeRequestId && state.recipeRequestId !== action.meta.requestId) return;

        const artworkId = action.payload?.id || action.meta.arg;
        const recipe = action.payload?.recipe || null;

        if (artworkId) {
          const prev = state.byId[artworkId] || { id: artworkId };
          state.byId[artworkId] = {
            ...prev,
            sampleRecipe: recipe || prev.sampleRecipe || null,
            nutritionalInfo: {
              ...(prev.nutritionalInfo || {}),
              ...((recipe && recipe.nutritionalInfo) || {}),
            },
            identifiedIngredients: prev.identifiedIngredients || [],
          };
          if (!state.allIds.includes(artworkId)) {
            state.allIds.unshift(artworkId);
          }
          state.selectedId = artworkId;
          state.currentViewMode = 'recipe';
        }

        state.recipeStatus = 'succeeded';
        state.recipeError = null;
        state.recipeRequestId = null;
        state.recipeForId = null;
      })
      .addCase(fetchRecipeForArtwork.rejected, (state, action) => {
        if (state.recipeRequestId && state.recipeRequestId !== action.meta.requestId) return;

        state.recipeStatus = 'failed';
        state.recipeError = action.payload || action.error?.message || 'Failed to generate recipe';
        state.recipeRequestId = null;
        state.recipeForId = null;
      });
  }
});

export const { setViewMode, selectArtworkId, clearError, addArtwork, removeArtwork } = artworksSlice.actions;
export const { updateRecipe, saveRecipe, removeSavedRecipe, setSavedRecipes } = artworksSlice.actions;
export default artworksSlice.reducer;

// ---------- selectors ----------
const selectArtState = (state) => state.artworks;

export const selectCurrentViewMode = (state) => selectArtState(state).currentViewMode;
export const selectArtworksError   = (state) => selectArtState(state).error;
export const selectStatus          = (state) => selectArtState(state).status;
export const selectArtworksStatus  = selectStatus;
export const selectRecipeStatus    = (state) => selectArtState(state).recipeStatus;
export const selectRecipeError     = (state) => selectArtState(state).recipeError;
export const selectSelectedId      = (state) => selectArtState(state).selectedId;
export const selectSelectedArtworkId = (state) => selectArtState(state).selectedId;
export const selectArtworkById     = (state, id) => (id ? selectArtState(state).byId[id] : null);
export const selectSavedRecipeIds  = (state) => selectArtState(state).savedRecipeIds;

export const selectAllArtworks = createSelector(
  [(state) => selectArtState(state).allIds, (state) => selectArtState(state).byId],
  (ids, byId) => ids.map(id => byId[id])
);

// For compatibility with your component import `selectArtwork`:
export const selectArtwork = createSelector(
  [selectArtState, selectSelectedId],
  (art, id) => (id ? art.byId[id] : null)
);

export const selectSavedRecipes = (state) => selectArtState(state).savedRecipes;
export const selectIsRecipeSaved = createSelector(
  [selectSavedRecipes, (_state, id) => id],
  (saved, id) => saved.some((entry) => entry.id === id)
);

// Often handy: grouped by cardinal slot for tabs
export const selectGroupedByCardinal = createSelector([selectAllArtworks], (arr) => {
  const groups = { Midnight: [], Morning: [], Noon: [], Evening: [] };
  for (const a of arr) {
    const slot = (a?.cardinalTime || '').toLowerCase();
    const label = slot === 'midnight' ? 'Midnight' :
                  slot === 'morning'  ? 'Morning'  :
                  slot === 'noon'     ? 'Noon'     : 'Evening';
    groups[label].push(a);
  }
  return groups;
});
